import type { StreamProvider } from '@vgtv/api-client';

import { getWebStorageItem, setWebStorageItem } from '../utils/storage';
import { Unpacked } from '../plugins/skin-vgtv2/utils/types';

export interface VariantDefinition<T extends string = string, P = unknown> {
  id: T;
  weight: number;
  payload?: P;
}

export interface Experiment {
  readonly id: string;
  readonly name: string;
  readonly provider?: StreamProvider;
  readonly variants: readonly VariantDefinition[];
}

export type Variant<T extends Experiment['variants']> =
  Unpacked<T> extends VariantDefinition<infer U> ? U : never;

export type Payload<T extends Experiment['variants']> =
  Unpacked<T> extends VariantDefinition<any, infer U> ? U : never;

export const EXPERIMENTS = {} as const;

const getRandomVariant = (variants: readonly VariantDefinition[]): string => {
  const target = Math.floor(Math.random() * 100);

  const variantIndex = variants
    .reduce<number[]>((acc, curr, index) => {
      const prev = acc[index - 1] ?? 0;

      acc.push(prev + curr.weight);

      return acc;
    }, [])
    .findIndex((threshold) => target <= threshold);

  return variants[variantIndex].id;
};

const getStorageKey = (provider: StreamProvider, id: string) => {
  return `${provider}/player/${id}`;
};

export const resolveExperiments = (configProvider: StreamProvider) => {
  const experiments = EXPERIMENTS as Record<string, Experiment>;
  const resolved = Object.keys(experiments)
    .filter(
      (e) =>
        !experiments[e].provider || experiments[e].provider === configProvider
    )
    .reduce<
      Record<
        string,
        { id: string; name: string; variant: string; payload: unknown }
      >
    >((acc, key) => {
      const { id, name, variants } =
        experiments[key as keyof typeof experiments];

      const currentVariant = getWebStorageItem<Variant<typeof variants>>(
        getStorageKey(configProvider, id)
      );

      const variant = currentVariant || getRandomVariant(variants);

      const getPayload = () => {
        const variantDefinition = variants.find(
          (definition) => definition.id === variant
        );

        if (variantDefinition && 'payload' in variantDefinition) {
          return variantDefinition.payload;
        }

        return undefined;
      };

      acc[key] = {
        id,
        name,
        variant,
        payload: getPayload(),
      };

      return acc;
    }, {});

  Object.values(resolved).forEach(({ id, variant }) => {
    setWebStorageItem(getStorageKey(configProvider, id), variant);
  });

  return resolved;
};
